import { EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Stack,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useHomeworkState from "../../state/homework.state";
import { BaseAudio } from "../../utils/audio";
import { hash } from "../../utils/common";
import AudioRecorder from "../AudioRecorder";
import CategoryManager, { CategoryMangerHandle } from "../CategoryManager";

type Props = {
  onSubmit: (values: BaseAudio) => void;
  defaultValue?: BaseAudio;
  buttonText?: string;
  isDisabled?: boolean;
};

export default function AudioForm({
  onSubmit,
  defaultValue,
  buttonText,
  isDisabled = false,
}: Props) {
  const { t } = useTranslation();
  const categoryManagerRef = useRef<CategoryMangerHandle>(null);
  const { categories } = useHomeworkState();
  const {
    handleSubmit,
    register,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isValid, isSubmitted },
  } = useForm<BaseAudio>();

  useEffect(() => {
    // The reset doesn't work as expect with the `Select` component so `setValue` is used instead
    setValue("startTime", defaultValue?.startTime ?? "");
    setValue("duration", defaultValue?.duration ?? 0);
    setValue("base64", defaultValue?.base64 ?? "");
    setValue("description", defaultValue?.description ?? "");
    setValue("categoryUuid", defaultValue?.categoryUuid ?? "");
  }, [defaultValue, setValue]);

  const _onSubmit = useCallback(
    (audio: BaseAudio) => {
      handleSubmit(() => onSubmit(audio))().then(() =>
        reset({
          startTime: "",
          duration: 0,
          base64: "",
          description: "",
          categoryUuid: "",
        }),
      );
    },
    [handleSubmit, onSubmit, reset],
  );

  return (
    <>
      <form onSubmit={handleSubmit(_onSubmit)}>
        <FormControl
          isDisabled={isDisabled}
          isInvalid={!isValid && isSubmitted}
          display="flex"
          flexDirection="column"
          gap={4}
        >
          <VStack alignItems="stretch">
            <Box flexGrow={1}>
              <FormLabel htmlFor="startTime">{t("forms.start")}</FormLabel>
              <Input
                id="startTime"
                type="time"
                step="1"
                placeholder="hh:mm:ss"
                {...register("startTime", {
                  required: t("forms.validation.required"),
                  pattern: {
                    value: /^(?:[01]\d|2[0-3]):(?:[0-5]\d):(?:[0-5]\d)$/,
                    message: `${t("forms.validation.invalid_format")} (hh:mm:ss)`,
                  },
                })}
              />
              <FormErrorMessage>
                {errors.startTime && errors.startTime.message}
              </FormErrorMessage>
            </Box>
            <Box flexGrow={1}>
              <FormLabel htmlFor="description">
                {t("forms.description")}
              </FormLabel>
              <Input
                {...register("description")}
                id="description"
                placeholder={t("forms.description")}
              />
              <FormErrorMessage>
                {errors.description && errors.description.message}
              </FormErrorMessage>
            </Box>
            <HStack alignItems="flex-end">
              <Box flexGrow={1}>
                <FormLabel htmlFor="category">{t("forms.category")}</FormLabel>
                <Select
                  placeholder={t("forms.select_category")}
                  id="category"
                  {...register("categoryUuid")}
                >
                  {Object.values(categories).map((category) => (
                    <option
                      key={`${category.uuid}${category.name}`}
                      style={{ color: category.color }}
                      value={category.uuid}
                    >
                      {category.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <IconButton
                isDisabled={isDisabled}
                onClick={() => categoryManagerRef.current?.onOpen()}
                aria-label="Edit categories"
                fontSize="18px"
                icon={<EditIcon />}
              />
            </HStack>
            <FormErrorMessage>
              {errors.categoryUuid && errors.categoryUuid.message}
            </FormErrorMessage>
            <Stack gap={4} flexGrow={1}>
              <FormLabel>{t("forms.recording")}</FormLabel>
              <Controller
                name="base64"
                control={control}
                defaultValue={defaultValue?.base64}
                rules={{ required: t("forms.validation.required") }}
                render={() => (
                  <HStack gap={4}>
                    <AudioRecorder
                      onChange={(base64: string, duration: number) => {
                        setValue("base64", base64);
                        setValue("duration", duration);
                      }}
                      isDisabled={isDisabled}
                    />
                    {watch("base64") && (
                      // The key must be updated to get the new source
                      <audio key={hash(watch("base64"))} controls>
                        <source src={watch("base64")} type="audio/ogg" />
                      </audio>
                    )}
                  </HStack>
                )}
              />
              <FormErrorMessage>
                {errors.base64 && errors.base64.message}
              </FormErrorMessage>
            </Stack>
          </VStack>
        </FormControl>
        <Button
          mt={4}
          bg="primary.DEFAULT"
          color="white"
          type="submit"
          // For some reason `isValid` is not working here (there are several related issues on github)
          isDisabled={!watch("base64") || isDisabled}
        >
          {buttonText ?? t("common.buttons.save")}
        </Button>
      </form>
      <CategoryManager ref={categoryManagerRef} />
    </>
  );
}
